// Apartados: a car held for 8 working hours (company_settings, not extendable)
// for a prospect who is still sending their documents. Rows live in
// `car_holds`, not reservations; see supabase/migrations/20261104_car_holds.sql
// and 20261106_company_settings_hold_hours.sql. A hold is live while it is
// neither released nor converted and expires_at is in the future — the same
// rule get_available_cars applies, so nothing needs to run when it expires.
import { useCallback, useEffect, useState } from "react";
import { Badge, BlockStack, Button, Card, InlineStack, Text } from "@shopify/polaris";
import supabase from "../../supabase-client";

export const HOLD_SELECT =
  "id, car_id, branch_id, date_range, label, draft, expires_at, extensions, created_at, " +
  "car:cars(plate, model:car_models(name, show_as)), seller:users!car_holds_created_by_fkey(name)";

export const isHoldLive = (h, now = Date.now()) =>
  !!h && !h.released_at && !h.converted_at && new Date(h.expires_at).getTime() > now;

// "15:30" today, "sáb 17 oct 14:00" otherwise.
export const holdTime = (iso) => {
  const d = new Date(iso);
  const time = d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === new Date().toDateString()) return time;
  return `${d.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" })} ${time}`;
};

export const minutesLeft = (h, now = Date.now()) =>
  Math.max(0, Math.ceil((new Date(h.expires_at).getTime() - now) / 60000));

// Clock time until expiry: "45 min", "3 h 10 min", "2 d 4 h".
export const timeLeft = (h, now = Date.now()) => {
  const m = minutesLeft(h, now);
  if (m < 60) return `${m} min`;
  if (m < 1440) return `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}`;
  return `${Math.floor(m / 1440)} d${Math.floor((m % 1440) / 60) ? ` ${Math.floor((m % 1440) / 60)} h` : ""}`;
};

// Stored ranges are Monterrey wall clock tagged as UTC: show them verbatim.
const rangeLabel = (range) => {
  const m = String(range || "").match(/^[[(]"?([^",]+)"?,\s*"?([^")\]]*)"?[)\]]$/);
  if (!m) return "";
  const fmt = (s) => s.substring(0, 16).replace("T", " ");
  const to = m[2] && m[2] !== "infinity" ? fmt(m[2]) : "indefinido";
  return `${fmt(m[1])} → ${to}`;
};

export async function fetchLiveHolds(branchId, userId) {
  let q = supabase
    .from("car_holds")
    .select(HOLD_SELECT)
    .is("released_at", null)
    .is("converted_at", null)
    .gt("expires_at", new Date().toISOString())
    .order("expires_at");
  if (branchId) q = q.eq("branch_id", branchId);
  if (userId) q = q.eq("created_by", userId);
  const { data, error } = await q;
  if (error) {
    console.error(error.message);
    return [];
  }
  return data || [];
}

export const releaseHold = (id) => supabase.rpc("release_car_hold", { p_hold_id: id });

// Re-renders every 30 s so countdowns stay current. Countdowns read the clock
// at render time, not this value, so two of them never disagree.
export function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  return now;
}

// The signed-in asesor's own live holds, with Continuar / Liberar. Anyone's
// hold can still be opened from the Ocupación timeline (?hold=ID).
export default function CarHolds({ branchId, userId, refreshKey, activeHoldId, onContinue, onChanged }) {
  const [holds, setHolds] = useState([]);
  const [busy, setBusy] = useState(null);
  useNow();

  const load = useCallback(
    async () => setHolds(userId ? await fetchLiveHolds(branchId, userId) : []),
    [branchId, userId]
  );
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const act = async (id, fn) => {
    setBusy(id);
    const { data, error } = await fn(id);
    setBusy(null);
    if (error) alert(error.message);
    await load();
    onChanged?.(data || null);
  };

  const live = holds.filter((h) => isHoldLive(h));
  if (!live.length) return null;

  return (
    <Card>
      <BlockStack gap="300">
        <Text variant="headingMd">Mis apartados</Text>
        {live.map((h) => {
          const left = minutesLeft(h);
          return (
            <InlineStack key={h.id} align="space-between" blockAlign="center" gap="300" wrap={false}>
              <BlockStack gap="050">
                <InlineStack gap="200" blockAlign="center">
                  <Text fontWeight="semibold">{h.label || "Apartado sin nombre"}</Text>
                  <Badge tone={left <= 60 ? "critical" : "attention"}>
                    {`Vence ${holdTime(h.expires_at)} · ${timeLeft(h)}`}
                  </Badge>
                  {h.id === activeHoldId && <Badge tone="info">En este formulario</Badge>}
                </InlineStack>
                <Text variant="bodySm" tone="subdued">
                  {[h.car?.plate, h.car?.model?.show_as || h.car?.model?.name].filter(Boolean).join(" · ")}
                  {" · "}
                  {rangeLabel(h.date_range)}
                </Text>
              </BlockStack>
              <InlineStack gap="200" wrap={false}>
                {h.id !== activeHoldId && (
                  <Button size="slim" onClick={() => onContinue?.(h)}>
                    Continuar
                  </Button>
                )}
                <Button
                  size="slim"
                  tone="critical"
                  variant="plain"
                  disabled={busy === h.id}
                  onClick={() => act(h.id, releaseHold)}
                >
                  Liberar
                </Button>
              </InlineStack>
            </InlineStack>
          );
        })}
      </BlockStack>
    </Card>
  );
}
